import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import { Column } from 'components/shared/Layout';
import { useDiscountEditSettings } from './hooks/useDiscountEditSettings';
import { VoucherStatus } from 'constants/vouchers';
import { StyledSelect } from '../VoucherDetails/VoucherDetails.styles';
import {
  FormContainer,
  StyledLabel,
  StyledPriceWrapper,
  StyledPriceLabel,
  StyledCurrencyInput,
  StyledTextInput,
  StyledDateInput,
  DatesWrapper,
  DateItem,
  DateSeparator,
  StyledDatesRow,
  StyledCreatedInfoRow,
  StyledRow
} from './DiscountCodeDetails.styles';

const DATE_DISPLAY_FORMAT = 'Do MMM YYYY';
const DATE_TIME_DISPLAY_FORMAT = 'Do MMM YYYY [at] HH:mm';
const DATE_FORMAT = 'YYYY-MM-DD';
const TRAVEL_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const getTravelDays = (travelDays) =>
  travelDays
    .split(',')
    .map((v, i) => (v === '1' ? TRAVEL_DAYS[i] : null))
    .filter((v) => v != null)
    .join(', ');

const DiscountCodeDetailsTable = ({
  businessAccount,
  createdFor,
  reference,
  type,
  valueType,
  voucherOriginalValue,
  voucherCurrency,
  voucherStatus,
  salesPeriodStart,
  salesPeriodEnd,
  travelPeriodStart,
  travelPeriodEnd,
  travelDays,
  createdBy,
  createdAt,
  updatedBy,
  updatedAt,
  activatedAt,
  inactivatedAt,
  canUpdateVoucher,
  isEditable,
  register,
  maxTotalDiscountAmount,
  minBookingValue,
  maxBookingValue
}) => {
  const editSettings = useDiscountEditSettings();
  const isDateEditable = isEditable && canUpdateVoucher;

  return (
    <FormContainer>
      <StyledRow columns={12}>
        <Column span={4}>
          <StyledLabel htmlFor="reference">
            <FormattedMessage id="discountCodes.reference" />
          </StyledLabel>
          <StyledTextInput name="reference" type="text" value={reference} readOnly data-cy="discount-code-reference" />
        </Column>
        <Column span={4}>
          <StyledLabel htmlFor="description">
            <FormattedMessage id="discountCodes.description" />
          </StyledLabel>
          <StyledTextInput name="description" type="text" value={type} readOnly data-cy="discount-code-description" />
        </Column>
        <Column span={4}>
          <StyledLabel htmlFor="status">
            <FormattedMessage id="discountCodes.status" />
          </StyledLabel>
          {editSettings.fields.status && (
            <StyledSelect {...register('voucherStatus', { required: true })} id="voucherStatus" data-cy="voucher-status-editable">
              <option key={VoucherStatus.ACTIVE} value={VoucherStatus.ACTIVE}>
                {VoucherStatus.ACTIVE}
              </option>
              <option key={VoucherStatus.INACTIVE} value={VoucherStatus.INACTIVE}>
                {VoucherStatus.INACTIVE}
              </option>
            </StyledSelect>
          )}
          {!editSettings.fields.status && (
            <StyledTextInput
              name="voucherStatus"
              type="text"
              value={VoucherStatus[voucherStatus.toUpperCase()]}
              readOnly
              data-cy="voucher-status-not-editable"
            />
          )}
        </Column>
      </StyledRow>
      <StyledRow columns={12}>
        <Column span={2}>
          <StyledLabel htmlFor="type">
            <FormattedMessage id="discountCodes.type" />
          </StyledLabel>
          <StyledTextInput name="type" type="text" value={valueType} readOnly data-cy="discount-code-type" />
        </Column>
        <Column span={2}>
          <StyledLabel htmlFor="currency">
            <FormattedMessage id="discountCodes.currency" />
          </StyledLabel>
          <StyledTextInput name="currency" type="text" value={voucherCurrency} readOnly data-cy="discount-code-currency" />
        </Column>
        <Column span={2}>
          {valueType === 'PERCENTAGE' ? (
            <>
              <StyledLabel htmlFor="value">
                <FormattedMessage id="discountCodes.value" />
              </StyledLabel>
              <StyledTextInput
                name="value"
                type="text"
                value={`${voucherOriginalValue.toString()}%`}
                readOnly
                data-cy="discount-code-value"
              />
            </>
          ) : (
            <StyledPriceWrapper>
              <StyledPriceLabel htmlFor="value">
                <FormattedMessage id="discountCodes.value" />
              </StyledPriceLabel>
              <StyledCurrencyInput
                currency={voucherCurrency}
                isControlled
                name="value"
                value={parseFloat(voucherOriginalValue).toFixed(2)}
                readOnly
                data-cy="discount-code-value"
              />
            </StyledPriceWrapper>
          )}
        </Column>
        <Column span={2}>
          <StyledLabel htmlFor="minBookingValue">
            <FormattedMessage id="discountCodes.minBookingValue" />
          </StyledLabel>
          <StyledTextInput
            name="minBookingValue"
            type="text"
            value={minBookingValue?.toFixed(2)}
            readOnly
            data-cy="discount-code-min-booking-value"
          />
        </Column>
        <Column span={2}>
          <StyledLabel htmlFor="maxBookingValue">
            <FormattedMessage id="discountCodes.maxBookingValue" />
          </StyledLabel>
          <StyledTextInput
            name="maxBookingValue"
            type="text"
            value={maxBookingValue?.toFixed(2)}
            readOnly
            data-cy="discount-code-max-booking-value"
          />
        </Column>
        <Column span={2}>
          <StyledLabel htmlFor="maxTotalDiscount">
            <FormattedMessage id="discountCodes.maxTotalDiscountAmount" />
          </StyledLabel>
          <StyledTextInput
            name="maxTotalDiscount"
            type="text"
            value={maxTotalDiscountAmount?.toFixed(2)}
            readOnly
            data-cy="discount-code-max-total-discount"
          />
        </Column>
      </StyledRow>
      <StyledDatesRow columns={12}>
        <Column span={4}>
          <DatesWrapper>
            <DateItem>
              <StyledLabel htmlFor="salesPeriodStart">
                <FormattedMessage id="vouchers.salesPeriodStart" />
              </StyledLabel>
              <StyledDateInput
                type="text"
                name="salesPeriodStart"
                value={moment(salesPeriodStart).format(DATE_DISPLAY_FORMAT)}
                readOnly
                data-cy="voucher-sales-start"
              />
            </DateItem>
            <DateSeparator>-</DateSeparator>
            <DateItem>
              <StyledLabel htmlFor="salesPeriodEnd">
                <FormattedMessage id="vouchers.salesPeriodEnd" />
              </StyledLabel>
              <StyledDateInput
                type="date"
                min={moment().format(DATE_FORMAT)}
                max={moment(salesPeriodEnd).add(1, 'year').format(DATE_FORMAT)}
                defaultValue={moment(salesPeriodEnd).format(DATE_FORMAT)}
                {...register('salesPeriodEnd', { required: true })}
                data-cy="voucher-sales-end-editable"
                hide={!isDateEditable || voucherStatus === 'REDEEMED'}
              />
              <StyledDateInput
                type="text"
                defaultValue={moment(salesPeriodEnd).format(DATE_DISPLAY_FORMAT)}
                readOnly
                data-cy="voucher-sales-end-readonly"
                hide={isDateEditable && voucherStatus !== 'REDEEMED'}
              />
            </DateItem>
          </DatesWrapper>
        </Column>
        <Column span={4}>
          <DatesWrapper>
            <DateItem>
              <StyledLabel htmlFor="travelPeriodStart">
                <FormattedMessage id="vouchers.travelPeriodStart" />
              </StyledLabel>
              <StyledDateInput
                type="text"
                name="travelPeriodStart"
                value={moment(travelPeriodStart).format(DATE_DISPLAY_FORMAT)}
                readOnly
                data-cy="voucher-travel-start"
              />
            </DateItem>
            <DateSeparator>-</DateSeparator>
            <DateItem>
              <StyledLabel htmlFor="travelPeriodEnd">
                <FormattedMessage id="vouchers.travelPeriodEnd" />
              </StyledLabel>
              <StyledDateInput
                type="text"
                name="travelPeriodEnd"
                value={moment(travelPeriodEnd).format(DATE_DISPLAY_FORMAT)}
                readOnly
                data-cy="voucher-travel-end"
              />
            </DateItem>
          </DatesWrapper>
        </Column>
        <Column span={4}>
          <StyledLabel htmlFor="travelDays">
            <FormattedMessage id="discountCodes.travelDays" />
          </StyledLabel>
          <StyledTextInput name="travelDays" type="text" value={getTravelDays(travelDays)} readOnly data-cy="discount-code-travel-days" />
        </Column>
      </StyledDatesRow>
      <StyledCreatedInfoRow columns={12}>
        <Column span={3}>
          <StyledLabel htmlFor="createdBy">
            <FormattedMessage id="vouchers.createdBy" />
          </StyledLabel>
          <StyledTextInput name="createdBy" type="text" value={createdBy} readOnly data-cy="voucher-created-by" />
        </Column>
        <Column span={3}>
          <StyledLabel htmlFor="createdAt">
            <FormattedMessage id="vouchers.createdAt" />
          </StyledLabel>
          <StyledTextInput
            type="text"
            name="createdAt"
            value={moment(createdAt).format(DATE_TIME_DISPLAY_FORMAT)}
            readOnly
            data-cy="voucher-created-at"
          />
        </Column>
        {createdFor && (
          <Column span={3}>
            <StyledLabel htmlFor="createdFor">
              <FormattedMessage id="vouchers.createdFor" />
            </StyledLabel>
            <StyledTextInput name="createdFor" type="text" value={createdFor} readOnly data-cy="voucher-created-for" />
          </Column>
        )}
        {businessAccount && (
          <Column span={3}>
            <StyledLabel htmlFor="businessAccount">
              <FormattedMessage id="vouchers.businessAccount" />
            </StyledLabel>
            <StyledTextInput name="businessAccount" type="text" value={businessAccount} readOnly data-cy="voucher-business-account" />
          </Column>
        )}
      </StyledCreatedInfoRow>
      {(updatedBy || updatedAt) && (
        <StyledRow columns={12}>
          {updatedBy && (
            <Column span={3}>
              <StyledLabel htmlFor="updatedBy">
                <FormattedMessage id="vouchers.updatedBy" />
              </StyledLabel>
              <StyledTextInput name="updatedBy" type="text" value={updatedBy} readOnly data-cy="voucher-updated-by" />
            </Column>
          )}
          {updatedAt && (
            <Column span={3}>
              <StyledLabel htmlFor="updatedAt">
                <FormattedMessage id="vouchers.updatedAt" />
              </StyledLabel>
              <StyledTextInput
                type="text"
                name="updatedAt"
                value={moment(updatedAt).format(DATE_TIME_DISPLAY_FORMAT)}
                readOnly
                data-cy="voucher-updated-at"
              />
            </Column>
          )}
        </StyledRow>
      )}
      {(activatedAt || inactivatedAt) && (
        <StyledRow columns={12}>
          {activatedAt && (
            <Column span={3}>
              <StyledLabel htmlFor="activatedAt">
                <FormattedMessage id="vouchers.activatedAt" />
              </StyledLabel>
              <StyledTextInput
                type="text"
                name="activatedAt"
                value={moment(activatedAt).format(DATE_TIME_DISPLAY_FORMAT)}
                readOnly
                data-cy="voucher-activated-at"
              />
            </Column>
          )}
          {inactivatedAt && (
            <Column span={3}>
              <StyledLabel htmlFor="inactivatedAt">
                <FormattedMessage id="vouchers.inactivatedAt" />
              </StyledLabel>
              <StyledTextInput
                type="text"
                name="inactivatedAt"
                value={moment(inactivatedAt).format(DATE_TIME_DISPLAY_FORMAT)}
                readOnly
                data-cy="voucher-inactivated-at"
              />
            </Column>
          )}
        </StyledRow>
      )}
    </FormContainer>
  );
};

DiscountCodeDetailsTable.propTypes = {
  createdFor: PropTypes.string,
  businessAccount: PropTypes.string,
  reference: PropTypes.string,
  type: PropTypes.string,
  valueType: PropTypes.string,
  voucherOriginalValue: PropTypes.number,
  voucherCurrency: PropTypes.string,
  voucherStatus: PropTypes.string,
  salesPeriodStart: PropTypes.string,
  salesPeriodEnd: PropTypes.string,
  travelPeriodStart: PropTypes.string,
  travelPeriodEnd: PropTypes.string,
  travelDays: PropTypes.string,
  createdBy: PropTypes.string,
  createdAt: PropTypes.string,
  updatedBy: PropTypes.string,
  updatedAt: PropTypes.string,
  activatedAt: PropTypes.string,
  inactivatedAt: PropTypes.string,
  canUpdateVoucher: PropTypes.bool,
  isEditable: PropTypes.bool,
  register: PropTypes.func,
  isActive: PropTypes.bool,
  maxTotalDiscountAmount: PropTypes.number,
  minBookingValue: PropTypes.number,
  maxBookingValue: PropTypes.number
};

export default DiscountCodeDetailsTable;
